<script setup>
import { computed, ref, watch } from 'vue'
import { useDashboardResource } from '@/composables/useDashboardResource'
import { useUnitDashboardFilters } from '@/composables/useUnitDashboardFilters'
import unitDashboardService from '@/services/feedback/unitDashboard.service'
import UnitBacklogAging from '@/views/feedback/dashboard/unit/UnitBacklogAging.vue'
import UnitComplaintRate from '@/views/feedback/dashboard/unit/UnitComplaintRate.vue'
import UnitDashboardFilterBar from '@/views/feedback/dashboard/unit/UnitDashboardFilterBar.vue'
import UnitDetractorOutcomes from '@/views/feedback/dashboard/unit/UnitDetractorOutcomes.vue'
import UnitFeedbackCoverage from '@/views/feedback/dashboard/unit/UnitFeedbackCoverage.vue'
import UnitDepartmentHeatmap from '@/views/feedback/dashboard/unit/UnitDepartmentHeatmap.vue'
import UnitKpiStrip from '@/views/feedback/dashboard/unit/UnitKpiStrip.vue'
import UnitPerformanceMatrix from '@/views/feedback/dashboard/unit/UnitPerformanceMatrix.vue'
import UnitVoiceCard from '@/views/feedback/dashboard/unit/UnitVoiceCard.vue'
import UnitSatisfactionTrend from '@/views/feedback/dashboard/unit/UnitSatisfactionTrend.vue'
import UnitResolutionTime from '@/views/feedback/dashboard/unit/UnitResolutionTime.vue'
import UnitScorecard from '@/views/feedback/dashboard/unit/UnitScorecard.vue'

definePage({
  meta: {
    name: 'dashboard-unit',
    subject: 'feedback.menu',
    action: 'dashboard_unit',
  },
})

const {
  dateRange,
  datePickerConfig,
  selectedUnitIds,
  selectedFormTypeId,
  formTypes,
  debouncedFilters,
} = useUnitDashboardFilters()

// summary and trend each feed several widgets, so the page loads them once
// and hands them down. The other three endpoints belong to one widget each
// and are loaded by that widget.
const summary = useDashboardResource(
  () => unitDashboardService.summary(debouncedFilters.value),
  { errorMessage: 'Could not load the unit summary' },
)

const trend = useDashboardResource(
  () => unitDashboardService.trend(debouncedFilters.value),
  { errorMessage: 'Could not load the unit trend' },
)

// summary follows every filter; trend only depends on the range END, the
// units and the form type.
watch(debouncedFilters, () => summary.load(), { deep: true, immediate: true })

watch(
  () => JSON.stringify([debouncedFilters.value.to, debouncedFilters.value.unitIds, debouncedFilters.value.formTypeId]),
  () => trend.load(),
  { immediate: true },
)

// The unit filter's options. The endpoint only returns the units it was
// asked for, so the list is captured from an UNFILTERED response and kept
// while a unit filter is active.
const allUnits = ref([])

watch(() => summary.data.value, payload => {
  if (!payload || debouncedFilters.value.unitIds.length) return

  allUnits.value = payload.units.map(u => ({ id: u.unit_id, name: u.unit_name }))
})

// The scorecard draws from both resources: the metrics come from summary and
// the sparklines from trend, so its refresh reloads both.
const reloadScorecard = () => {
  summary.load()
  trend.load()
}

const periodCaption = computed(() => {
  const w = summary.data.value?.window

  if (!w || !summary.hasFreshData.value || summary.isLoading.value) return ''

  return `Changes compare with ${w.previous_from} to ${w.previous_to}`
})
</script>

<template>
  <div>
    <div class="d-flex flex-column flex-md-row justify-space-between align-stretch align-md-center gap-4 mb-6">
      <div>
        <h1 class="text-h4 font-weight-bold">
          Unit Dashboard
        </h1>
        <p class="text-body-1 text-medium-emphasis mb-0">
          How each unit is performing, and where to look first
          <span v-if="periodCaption"> · {{ periodCaption }}</span>
        </p>
      </div>

      <UnitDashboardFilterBar
        v-model:unit-ids="selectedUnitIds"
        v-model:form-type-id="selectedFormTypeId"
        v-model:date-range="dateRange"
        :units="allUnits"
        :form-types="formTypes"
        :date-picker-config="datePickerConfig"
      />
    </div>

    <VRow>
      <!--
        Inside the page's row, not above it: a row of its own carried an
        extra bottom margin on top of the column gutters and sat further
        from the scorecard than the cards sit from each other.
      -->
      <VCol cols="12">
        <UnitKpiStrip
          :summary="summary.data.value"
          :loading="summary.isLoading.value"
          :error="summary.error.value"
          @retry="summary.load"
        />
      </VCol>

      <VCol cols="12">
        <UnitScorecard
          :summary="summary.data.value"
          :trend="trend.hasFreshData.value && !trend.isLoading.value ? trend.data.value : null"
          :loading="summary.isLoading.value"
          :error="summary.error.value"
          @retry="reloadScorecard"
        />
      </VCol>

      <!-- Directly under the scorecard: how many customers its scores rest on. -->
      <VCol cols="12">
        <UnitFeedbackCoverage :filters="debouncedFilters" />
      </VCol>

      <VCol
        cols="12"
        lg="5"
      >
        <UnitPerformanceMatrix
          :summary="summary.data.value"
          :loading="summary.isLoading.value"
          :error="summary.error.value"
          @retry="summary.load"
        />
      </VCol>
      <VCol
        cols="12"
        lg="7"
      >
        <UnitSatisfactionTrend
          :trend="trend.data.value"
          :loading="trend.isLoading.value"
          :error="trend.error.value"
          @retry="trend.load"
        />
      </VCol>

      <VCol cols="12">
        <UnitComplaintRate
          :summary="summary.data.value"
          :loading="summary.isLoading.value"
          :error="summary.error.value"
          @retry="summary.load"
        />
      </VCol>
      <VCol
        cols="12"
        md="6"
      >
        <UnitBacklogAging :filters="debouncedFilters" />
      </VCol>
      <VCol
        cols="12"
        md="6"
      >
        <UnitResolutionTime :filters="debouncedFilters" />
      </VCol>

      <VCol cols="12">
        <UnitDetractorOutcomes :filters="debouncedFilters" />
      </VCol>

      <VCol cols="12">
        <UnitVoiceCard :filters="debouncedFilters" />
      </VCol>

      <VCol cols="12">
        <UnitDepartmentHeatmap :filters="debouncedFilters" />
      </VCol>
    </VRow>
  </div>
</template>
